import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FaTruck, FaSearch, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import './LiveBuyForm.css';

const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhone = (phone) => {
  const phoneRegex = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;
  return phoneRegex.test(phone);
};

const validateZip = (zip) => {
  const zipRegex = /^[0-9]{5}$/;
  return zipRegex.test(zip);
};

const LiveBuyForm = () => {
  const { publicToken } = useParams();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [step, setStep] = useState(1);
  const [activeColorSelections, setActiveColorSelections] = useState({});

  const [customerData, setCustomerData] = useState(() => {
    const savedData = localStorage.getItem('shippingInformation');
    return savedData ? JSON.parse(savedData) : {
      email: '',
      shipping_address: {
        first_name: '',
        last_name: '',
        address1: '',
        address2: '',
        city: '',
        country: 'France',
        state: '',
        zip: '',
        phone: ''
      },
      click_and_collect: false,
      is_relay: false,
      relay_id: null
    };
  });

  const [validationErrors, setValidationErrors] = useState({
    email: '',
    phone: '',
    zip: '',
    relay: ''
  });

  const [paymentIntent, setPaymentIntent] = useState(null);
  const [stripePublicKey, setStripePublicKey] = useState(null);
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentMessage, setPaymentMessage] = useState('');

  const [isRepeatCustomer, setIsRepeatCustomer] = useState(false);

  const [relayPoints, setRelayPoints] = useState([]);
  const [loadingRelayPoints, setLoadingRelayPoints] = useState(false);
  const [relayError, setRelayError] = useState(null);
  const [selectedRelay, setSelectedRelay] = useState(null);

  const mapRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [geocoderInstance, setGeocoderInstance] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [infoWindow, setInfoWindow] = useState(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [mapApiError, setMapApiError] = useState(null);
  const GOOGLE_MAPS_API_KEY = 'AIzaSyCyvKy9iw2hBuxvJkst5iY8rA7fERdASpE';

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const response = await axios.get(`/api/shopify/public/form/${publicToken}`);
        setFormData(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load form');
      } finally {
        setLoading(false);
      }
    };

    fetchFormData();
  }, [publicToken]);

  useEffect(() => {
    if (!formData || !searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    
    const filtered = formData.products.filter(product =>
      product.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setSearchResults(filtered);
  }, [searchQuery, formData]);

  useEffect(() => {
    const variantsForApi = [];
    selectedProducts.forEach(product => {
      if (product.configuredVariants && product.configuredVariants.length > 0) {
        product.configuredVariants.forEach(cv => {
          variantsForApi.push({
            variant_id: cv.variantData.id,
            quantity: cv.quantity
          });
        });
      }
    });
    setSelectedVariants(variantsForApi);
  }, [selectedProducts]);

  useEffect(() => {
    if (stripePublicKey && !stripe && window.Stripe) {
      console.log('Initializing Stripe with key:', stripePublicKey);
      const stripeInstance = window.Stripe(stripePublicKey);
      setStripe(stripeInstance);
    }
  }, [stripePublicKey, stripe]);

  useEffect(() => {
    if (window.google && window.google.maps) {
      setGoogleMapsLoaded(true);
      return;
    }

    const scriptId = 'google-maps-script';
    if (document.getElementById(scriptId)) {
        if (window.google && window.google.maps) {
            setGoogleMapsLoaded(true);
        }
        return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geocoding&callback=initMap`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      console.error("Google Maps script failed to load.");
      setMapApiError("Failed to load Google Maps. Please try again later.");
    };
    
    window.initMap = () => {
      console.log("Google Maps script loaded successfully.");
      setGoogleMapsLoaded(true);
    };

    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        // Optional: document.head.removeChild(existingScript);
      }
      delete window.initMap;
    };
  }, [GOOGLE_MAPS_API_KEY]);

  useEffect(() => {
    if (googleMapsLoaded && customerData.is_relay && mapRef.current && !mapInstance) {
      try {
        const map = new window.google.maps.Map(mapRef.current, {
          center: { lat: 46.2276, lng: 2.2137 },
          zoom: 6,
        });
        setMapInstance(map);
        setGeocoderInstance(new window.google.maps.Geocoder());
        setInfoWindow(new window.google.maps.InfoWindow());
        console.log("Map initialized");
      } catch(err) {
        console.error("Error initializing Google Map:", err);
        setMapApiError("Could not initialize the map.");
      }
    }
  }, [googleMapsLoaded, customerData.is_relay, mapInstance]);

  useEffect(() => {
      if (customerData.is_relay && mapInstance && geocoderInstance && customerData.shipping_address.zip && validateZip(customerData.shipping_address.zip)) {
          const address = `${customerData.shipping_address.zip}, ${customerData.shipping_address.country}`;
          geocoderInstance.geocode({ address: address }, (results, status) => {
              if (status === 'OK' && results?.[0]?.geometry?.location) {
                  mapInstance.setCenter(results[0].geometry.location);
                  mapInstance.setZoom(12);
                  fetchRelayPoints(customerData.shipping_address.zip);
              } else {
                  console.warn(`Geocode was not successful for the following reason: ${status}`);
                  fetchRelayPoints(customerData.shipping_address.zip);
              }
          });
      } else if (!customerData.is_relay && markers.length > 0) {
         markers.forEach(marker => marker.setMap(null));
         setMarkers([]);
         setSelectedRelay(null);
      }
  }, [
      customerData.is_relay,
      mapInstance,
      geocoderInstance,
      customerData.shipping_address.zip,
      customerData.shipping_address.country,
  ]);

  const handleProductSelect = (product) => {
    if (!selectedProducts.find(p => p.id === product.id)) {
      setSelectedProducts([...selectedProducts, {
        ...product,
        configuredVariants: [] 
      }]);
    }
    setSearchQuery('');
    setShowSearch(false);
  };

  const handleVariantToggle = (productId, variantData) => {
    setSelectedProducts(prevProducts =>
      prevProducts.map(p => {
        if (p.id === productId) {
          const existingConfiguredVariantIndex = p.configuredVariants.findIndex(
            cv => cv.variantData.id === variantData.id
          );
          let newConfiguredVariants;

          if (existingConfiguredVariantIndex > -1) {
            // Variant already configured, remove it (toggle off)
            newConfiguredVariants = p.configuredVariants.filter(
              cv => cv.variantData.id !== variantData.id
            );
          } else {
            // Variant not configured, add it with quantity 1
            newConfiguredVariants = [
              ...p.configuredVariants,
              { variantData: variantData, quantity: 1 }
            ];
          }
          return { ...p, configuredVariants: newConfiguredVariants };
        }
        return p;
      })
    );
  };

  const handleConfiguredVariantQuantityChange = (productId, variantId, change) => {
    setSelectedProducts(prevProducts =>
      prevProducts.map(p => {
        if (p.id === productId) {
          const newConfiguredVariants = p.configuredVariants.map(cv => {
            if (cv.variantData.id === variantId) {
              return { ...cv, quantity: Math.max(1, cv.quantity + change) };
            }
            return cv;
          });
          return { ...p, configuredVariants: newConfiguredVariants };
        }
        return p;
      })
    );
  };

  const handleRemoveConfiguredVariant = (productId, variantId) => {
    setSelectedProducts(prevProducts =>
      prevProducts.map(p => {
        if (p.id === productId) {
          const newConfiguredVariants = p.configuredVariants.filter(
            cv => cv.variantData.id !== variantId
          );
          return { ...p, configuredVariants: newConfiguredVariants };
        }
        return p;
      })
    );
  };

  const handleCustomerDataChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setCustomerData(prev => {
        const newData = {
          ...prev,
          [parent]: { ...prev[parent], [child]: value }
        };
        
        if (field === 'is_relay' && !value) {
          newData.relay_id = null;
          setSelectedRelay(null);
          setValidationErrors(prev => ({...prev, relay: ''}));
        }
        
        if (field === 'is_relay' && value) {
          newData.click_and_collect = false;
        }
        
        if (field === 'click_and_collect' && value) {
          newData.is_relay = false;
          newData.relay_id = null;
          setSelectedRelay(null);
          setValidationErrors(prev => ({...prev, relay: ''}));
        }
        
        localStorage.setItem('shippingInformation', JSON.stringify(newData));
        return newData;
      });
    } else {
      setCustomerData(prev => {
        const newData = { ...prev, [field]: value };
        
        if (field === 'is_relay' && !value) {
          newData.relay_id = null;
          setSelectedRelay(null);
          setValidationErrors(prev => ({...prev, relay: ''}));
        }
        
        if (field === 'is_relay' && value) {
          newData.click_and_collect = false;
        }
        
        if (field === 'click_and_collect' && value) {
          newData.is_relay = false;
          newData.relay_id = null;
          setSelectedRelay(null);
          setValidationErrors(prev => ({...prev, relay: ''}));
        }
        
        localStorage.setItem('shippingInformation', JSON.stringify(newData));
        return newData;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {
      email: validateEmail(customerData.email) ? '' : 'Format email invalide',
      phone: validatePhone(customerData.shipping_address.phone) ? '' : 'Format téléphone invalide',
      zip: validateZip(customerData.shipping_address.zip) ? '' : 'Code postal invalide'
    };

    setValidationErrors(errors);

    if (Object.values(errors).some(error => error) || selectedVariants.length === 0) {
      return;
    }

    if (customerData.is_relay && !customerData.relay_id) {
      setValidationErrors(prev => ({...prev, relay: 'Veuillez sélectionner un point relais'}));
      return;
    }

    try {
      const preparePaymentData = {
        list_id: formData.list_id,
        variants: selectedVariants,
        customer_email: customerData.email,
        shipping_address: {
          name: `${customerData.shipping_address.first_name} ${customerData.shipping_address.last_name}`,
          line1: customerData.shipping_address.address1,
          line2: customerData.shipping_address.address2 || '',
          city: customerData.shipping_address.city,
          state: customerData.shipping_address.state || '',
          postal_code: customerData.shipping_address.zip,
          country: customerData.shipping_address.country
        }
      };

      console.log('Preparing payment with data:', preparePaymentData);
      
      const prepareResponse = await axios.post(`/api/shopify/public/form/${publicToken}/prepare_payment`, preparePaymentData);

      if (!prepareResponse.data || !prepareResponse.data.payment_intent_id || !prepareResponse.data.stripe_public_key) {
        throw new Error('Invalid prepare payment response: Missing required data');
      }

      console.log('Payment preparation successful:', prepareResponse.data);

      setStripePublicKey(prepareResponse.data.stripe_public_key);
      setIsRepeatCustomer(prepareResponse.data.is_repeat_customer || false);

      if (!window.Stripe) {
        throw new Error('Stripe.js not loaded');
      }

      let stripeInstance = stripe;
      if (!stripeInstance) {
        stripeInstance = window.Stripe(prepareResponse.data.stripe_public_key);
        setStripe(stripeInstance);
      }

      setPaymentIntent(prepareResponse.data.payment_intent_id);

      const paymentIntentData = {
        payment_intent_id: prepareResponse.data.payment_intent_id,
        shipping_address: {
          name: `${customerData.shipping_address.first_name} ${customerData.shipping_address.last_name}`,
          line1: customerData.shipping_address.address1,
          line2: customerData.shipping_address.address2 || '',
          city: customerData.shipping_address.city,
          state: customerData.shipping_address.state || '',
          postal_code: customerData.shipping_address.zip,
          country: customerData.shipping_address.country
        },
        click_and_collect: customerData.click_and_collect,
        is_relay: customerData.is_relay,
        relay_id: customerData.relay_id
      };

      console.log('Creating payment intent with data:', paymentIntentData);

      const paymentIntentResponse = await axios.post(
        `/api/shopify/public/form/${publicToken}/create_payment_intent`,
        paymentIntentData
      );

      if (paymentIntentResponse.data.click_and_collect) {
        setPaymentStatus('success');
        setPaymentMessage('Your order has been placed for in-store pickup. Thank you!');
        
        setTimeout(() => {
          setPaymentStatus(null);
          setPaymentMessage('');
          setPaymentIntent(null);
          setSelectedProducts([]);
          setStep(1);
        }, 3000);
        
        return;
      }

      if (!paymentIntentResponse.data || !paymentIntentResponse.data.client_secret) {
        throw new Error('Invalid payment intent response: Missing client_secret');
      }

      const options = {
        clientSecret: paymentIntentResponse.data.client_secret,
        appearance: {
          theme: 'stripe'
        }
      };

      const elementsInstance = stripeInstance.elements(options);
      if (!elementsInstance) {
        throw new Error('Failed to create Stripe elements instance');
      }

      const paymentElement = elementsInstance.create('payment');
      if (!paymentElement) {
        throw new Error('Failed to create payment element');
      }

      paymentElement.mount('#payment-element');
      setElements(elementsInstance);

    } catch (err) {
      console.error('Payment preparation error:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        fullError: err
      });
      setError(
        err.response?.data?.detail || 
        err.message || 
        'Failed to prepare payment. Please check the console for more details.'
      );
    }
  };

  const handlePaymentConfirmation = async () => {
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setPaymentError(null);
    setPaymentStatus(null);
    setPaymentMessage('');

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (error) {
        setPaymentStatus('failed');
        setPaymentMessage(error.message);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        setPaymentStatus('success');
        setPaymentMessage('Payment successful! Thank you for your purchase.');
        
        setTimeout(() => {
          setPaymentStatus(null);
          setPaymentMessage('');
          setPaymentIntent(null);
          setSelectedProducts([]);
          setStep(1);
        }, 3000);
      }
    } catch (err) {
      setPaymentStatus('failed');
      setPaymentMessage('An unexpected error occurred.');
    } finally {
      setProcessing(false);
    }
  };

  const renderVariantOptions = (product) => {
    const variants = product.variants;
    const colors = [...new Set(variants.map(v => v.title.split(' / ')[0]))];
    const sizes = [...new Set(variants.map(v => v.title.split(' / ')[1]).filter(Boolean))];

    const activeSelectedColorForProduct = activeColorSelections[product.id];

    return (
      <div className="live-buy-variants">
        <div className="live-buy-variant-colors">
          <h4 className="live-buy-variant-title">Color:</h4>
          <div className="live-buy-variant-options">
            {colors.map(color => {
              const isCurrentlyActiveColor = activeSelectedColorForProduct === color;
              // Check if any *configured* variant for this product starts with this color.
              // This is for visual feedback if a variant with this color is already in "Your Selections".
              const isConfiguredWithThisColor = product.configuredVariants?.some(cv => cv.variantData.title.startsWith(color));

              return (
                <button
                  key={color}
                  className={`live-buy-variant-button ${
                    isCurrentlyActiveColor || (sizes.length === 0 && isConfiguredWithThisColor) ? 'selected' : ''
                  }`}
                  onClick={() => {
                    if (sizes.length > 0) {
                      // If sizes exist, just set this color as active for this product
                      setActiveColorSelections(prev => ({ ...prev, [product.id]: color }));
                    } else {
                      // If no sizes, this color directly represents a variant. Toggle it.
                      const targetVariant = variants.find(v => v.title === color && v.available);
                      if (targetVariant) {
                        handleVariantToggle(product.id, targetVariant);
                      }
                    }
                  }}
                  // A color is disabled if no available variants start with this color.
                  disabled={!variants.some(v => v.title.startsWith(color) && v.available)}
                >
                  {color}
                </button>
              );
            })}
          </div>
        </div>

        {sizes.length > 0 && (
          <div className="live-buy-variant-sizes">
            <h4 className="live-buy-variant-title">Size:</h4>
            <div className="live-buy-variant-options">
              {sizes.map(size => {
                const colorToMatch = activeSelectedColorForProduct || colors.find(c => variants.some(v => v.title.startsWith(c) && v.available)) || colors[0];
                const variantWithSize = variants.find(v => v.title === `${colorToMatch} / ${size}`);
                
                return (
                  <button
                    key={size}
                    className={`live-buy-variant-button ${
                      product.configuredVariants?.some(cv => cv.variantData.id === variantWithSize?.id) ? 'selected' : ''
                    }`}
                    onClick={() => {
                      if (variantWithSize && variantWithSize.available) {
                        handleVariantToggle(product.id, variantWithSize);
                      }
                    }}
                    disabled={!variantWithSize?.available}
                  >
                    {size}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const fetchRelayPoints = async (zipCode) => {
    if (!mapInstance || !zipCode || !validateZip(zipCode)) return;

    setLoadingRelayPoints(true);
    setRelayError(null);
    setRelayPoints([]);

    markers.forEach(marker => marker.setMap(null));
    setMarkers([]);
    if (infoWindow) infoWindow.close();

    try {
      const response = await axios.get(
        `/api/shopify/public/form/${publicToken}/relay_points/${zipCode}`,
        { params: { carrier: 'mondialrelay', country: 'FR' } }
      );

      const fetchedRelays = response.data.relays || [];
      setRelayPoints(fetchedRelays);
      console.log('Fetched relay points:', fetchedRelays);

      if (fetchedRelays.length === 0) {
         setRelayError('Aucun point de relais trouvé pour ce code postal');
         return;
      }

      const newMarkers = [];
      const bounds = new window.google.maps.LatLngBounds();

      for (const relay of fetchedRelays) {
         if (relay.latitude != null && relay.longitude != null) {
             const position = { lat: parseFloat(relay.latitude), lng: parseFloat(relay.longitude) };
             const marker = new window.google.maps.Marker({
                 position: position,
                 map: mapInstance,
                 title: relay.name,
             });

             marker.addListener('click', () => {
                 handleRelaySelect(relay);
                 if (infoWindow) {
                     infoWindow.setContent(`
                         <div><strong>${relay.name}</strong></div>
                         <div>${relay.address1 || ''}</div>
                         ${relay.address2 ? `<div>${relay.address2}</div>` : ''}
                         <div>${relay.postcode || ''} ${relay.city || ''}</div>
                         <button id="select-relay-btn-${relay.id}">Sélectionner ce relais</button>
                     `);
                     infoWindow.open(mapInstance, marker);

                     infoWindow.addListener('domready', () => {
                        const button = document.getElementById(`select-relay-btn-${relay.id}`);
                        if (button) {
                            button.addEventListener('click', () => {
                                console.log("Button clicked for relay:", relay.id)
                                infoWindow.close();
                            });
                        }
                     });
                 }
                 mapInstance.panTo(position);
             });

             newMarkers.push(marker);
             bounds.extend(position);
         } else {
             console.warn(`Relay point ${relay.id || relay.name} is missing coordinates.`);
         }
      }

      setMarkers(newMarkers);

      if (newMarkers.length > 1) {
        mapInstance.fitBounds(bounds);
      } else if (newMarkers.length === 1) {
        mapInstance.setCenter(bounds.getCenter());
        mapInstance.setZoom(14);
      }

    } catch (err) {
      console.error('Error fetching or processing relay points:', err);
      setRelayError(err.response?.data?.detail || 'Failed to fetch relay points');
      setRelayPoints([]);
      markers.forEach(marker => marker.setMap(null));
      setMarkers([]);
    } finally {
      setLoadingRelayPoints(false);
    }
  };

  const handleRelaySelect = (relay) => {
    setSelectedRelay(relay);
    const relayId = relay.id || relay.ID || relay.relayId || relay.relay_id;

    if (relayId) {
      handleCustomerDataChange('relay_id', relayId);
      console.log(`Selected relay point via map: ${relay.name} with ID: ${relayId}`);
      markers.forEach(m => {
      });
      if (infoWindow) infoWindow.close();

    } else {
      console.error('Could not determine relay ID from selected map marker:', relay);
    }
  };

  // Determine if all methods should be shown by default
  const showAllShippingMethods = !formData || !formData.shipping_methods || formData.shipping_methods.length === 0;

  const isShippingMethodAvailable = (method) => {
    if (showAllShippingMethods) return true;
    return formData.shipping_methods.includes(method);
  };

  if (loading) {
    return (
      <div className="live-buy-loading-screen">
        <div className="live-buy-loading-content">
          <div className="live-buy-loading-logo">
            <img src="/bgless_logo.png" alt="Unanimity AI Logo" />
          </div>
          <h2>Chargement...</h2>
          <div className="live-buy-loading-progress">
            <div className="live-buy-progress-bar">
              <div className="live-buy-progress-glow"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) return (
    <div className="live-buy-wrapper">
      <div className="live-buy-container">
        <div className="live-buy-header">
          <h1>
            {error === 'Form not found or inactive' ? "This Live hasn't started yet" : error}
          </h1>
        </div>
      </div>
    </div>
  );

  return (
    <div className="live-buy-wrapper">
      <div className="live-buy-container">
        <div className="live-buy-header">
          <h1>{formData.list_name}</h1>
          {formData.description && <p>{formData.description}</p>}
        </div>

        {step === 1 && (
          <>
            <div className="live-buy-search-container">
              <div className="live-buy-search-wrapper">
                <FaSearch className="live-buy-search-icon" />
                <input
                  type="text"
                  className="live-buy-search-input"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setShowSearch(true)}
                />
              </div>
              
              {showSearch && searchResults.length > 0 && (
                <div className="live-buy-search-results">
                  {searchResults.map(product => (
                    <div
                      key={product.id}
                      className="live-buy-search-item"
                      onClick={() => handleProductSelect(product)}
                    >
                      {product.images?.[0] && (
                        <img
                          src={product.images[0].url}
                          alt={product.title}
                          className="live-buy-search-item-image"
                        />
                      )}
                      <div className="live-buy-search-item-info">
                        <span className="live-buy-search-item-title">{product.title}</span>
                        <span className="live-buy-search-item-price">{product.price} €</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="live-buy-selected-products">
              {selectedProducts.map(product => (
                <div key={product.id} className="live-buy-product">
                  <div className="live-buy-product-header">
                    <div className="live-buy-product-info">
                      <img
                        src={product.images[0]?.url}
                        alt={product.title}
                        className="live-buy-product-image"
                      />
                      <h3 className="live-buy-product-title">{product.title}</h3>
                    </div>
                    <button
                      className="live-buy-remove"
                      onClick={() => setSelectedProducts(prev => 
                        prev.filter(p => p.id !== product.id)
                      )}
                    >
                      &times;
                    </button>
                  </div>

                  {renderVariantOptions(product)}

                  {/* Display configured variants for this product */}
                  {product.configuredVariants && product.configuredVariants.length > 0 && (
                    <div className="live-buy-configured-variants-list">
                      <h4 className="live-buy-configured-variants-title">Your Selections:</h4>
                      {product.configuredVariants.map(cv => (
                        <div key={cv.variantData.id} className="live-buy-configured-variant-item">
                          <div className="live-buy-configured-variant-info">
                            <img 
                              src={cv.variantData.image?.url || product.images[0]?.url} 
                              alt={cv.variantData.title} 
                              className="live-buy-configured-variant-image" 
                            />
                            <div className="live-buy-configured-variant-details">
                              <span className="live-buy-configured-variant-name">{cv.variantData.title}</span>
                              <span className="live-buy-configured-variant-price">{cv.variantData.price} €</span>
                            </div>
                          </div>
                          <div className="live-buy-quantity-controls">
                            <button 
                              onClick={() => handleConfiguredVariantQuantityChange(product.id, cv.variantData.id, -1)} 
                              disabled={cv.quantity <= 1}
                            >
                              -
                            </button>
                            <span>{cv.quantity}</span>
                            <button onClick={() => handleConfiguredVariantQuantityChange(product.id, cv.variantData.id, 1)}>+</button>
                          </div>
                          <button 
                            className="live-buy-configured-variant-remove"
                            onClick={() => handleRemoveConfiguredVariant(product.id, cv.variantData.id)}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {selectedProducts.length > 0 && (
              <button 
                className="live-buy-button live-buy-button-primary"
                onClick={() => setStep(2)}
              >
                Continuer à la livraison
              </button>
            )}
          </>
        )}
        {step === 2 && (
          <form onSubmit={handleSubmit} className="live-buy-customer">
            <h2 className="live-buy-customer-title">Informations de livraison</h2>
            
            <div className="live-buy-form-group">
              <label className="live-buy-label">Email</label>
              <input
                type="email"
                className="live-buy-input"
                value={customerData.email}
                onChange={(e) => handleCustomerDataChange('email', e.target.value)}
                required
              />
              {validationErrors.email && <div className="live-buy-error">{validationErrors.email}</div>}
            </div>
            
            <div className="live-buy-form-group">
              <label className="live-buy-label">Prénom</label>
              <input
                type="text"
                className="live-buy-input"
                value={customerData.shipping_address.first_name}
                onChange={(e) => handleCustomerDataChange('shipping_address.first_name', e.target.value)}
                required
              />
            </div>
            
            <div className="live-buy-form-group">
              <label className="live-buy-label">Nom</label>
              <input
                type="text"
                className="live-buy-input"
                value={customerData.shipping_address.last_name}
                onChange={(e) => handleCustomerDataChange('shipping_address.last_name', e.target.value)}
                required
              />
            </div>
            
            <div className="live-buy-form-group">
              <label className="live-buy-label">Phone</label>
              <input
                type="text"
                className="live-buy-input"
                value={customerData.shipping_address.phone}
                onChange={(e) => handleCustomerDataChange('shipping_address.phone', e.target.value)}
                required
              />
              {validationErrors.phone && <div className="live-buy-error">{validationErrors.phone}</div>}
            </div>

            <div className="live-buy-delivery-options">
              <h3 className="live-buy-delivery-title">Options de livraison</h3>
              
              {isShippingMethodAvailable('home_delivery') && (
                <div className="live-buy-delivery-option">
                  <input
                    type="radio"
                    id="home-delivery"
                    name="delivery-option"
                    checked={!customerData.click_and_collect && !customerData.is_relay}
                    onChange={() => {
                      handleCustomerDataChange('click_and_collect', false);
                      handleCustomerDataChange('is_relay', false);
                    }}
                  />
                  <label htmlFor="home-delivery">Livraison à domicile</label>
                </div>
              )}
              
              {isShippingMethodAvailable('click_and_collect') && (
                <div className="live-buy-delivery-option">
                  <input
                    type="radio"
                    id="click-and-collect"
                    name="delivery-option"
                    checked={customerData.click_and_collect}
                    onChange={() => handleCustomerDataChange('click_and_collect', true)}
                  />
                  <label htmlFor="click-and-collect">Click and Collect</label>
                </div>
              )}
              
              {isShippingMethodAvailable('relay_point') && (
                <div className="live-buy-delivery-option">
                  <input
                    type="radio"
                    id="relay-point"
                    name="delivery-option"
                    checked={customerData.is_relay}
                    onChange={() => handleCustomerDataChange('is_relay', true)}
                  />
                  <label htmlFor="relay-point">Point Relais</label>
                </div>
              )}
            </div>
            
            <div className="live-buy-address-fields">
              <div className="live-buy-form-group">
                <label className="live-buy-label">Adresse</label>
                <input
                  type="text"
                  className="live-buy-input"
                  value={customerData.shipping_address.address1}
                  onChange={(e) => handleCustomerDataChange('shipping_address.address1', e.target.value)}
                  required
                />
              </div>
              
              <div className="live-buy-form-group">
                <label className="live-buy-label">Adresse ligne 2 (Optionnel)</label>
                <input
                  type="text"
                  className="live-buy-input"
                  value={customerData.shipping_address.address2}
                  onChange={(e) => handleCustomerDataChange('shipping_address.address2', e.target.value)}
                />
              </div>
              
              <div className="live-buy-form-group">
                <label className="live-buy-label">Code Postal</label>
                <input
                  type="text"
                  className="live-buy-input"
                  value={customerData.shipping_address.zip}
                  onChange={(e) => handleCustomerDataChange('shipping_address.zip', e.target.value)}
                  required
                />
                {validationErrors.zip && <div className="live-buy-error">{validationErrors.zip}</div>}
              </div>
              
              <div className="live-buy-form-group">
                <label className="live-buy-label">Ville</label>
                <input
                  type="text"
                  className="live-buy-input"
                  value={customerData.shipping_address.city}
                  onChange={(e) => handleCustomerDataChange('shipping_address.city', e.target.value)}
                  required
                />
              </div>
              
              <div className="live-buy-form-group">
                <label className="live-buy-label">Pays</label>
                <input
                  type="text"
                  className="live-buy-input"
                  value={customerData.shipping_address.country}
                  onChange={(e) => handleCustomerDataChange('shipping_address.country', e.target.value)}
                  required
                />
              </div>
            </div>

            {customerData.is_relay && isShippingMethodAvailable('relay_point') ? (
              <div className="live-buy-relay-section">
                <h3 className="live-buy-relay-title">Sélectionner un point de relais sur la carte</h3>

                <div ref={mapRef} style={{ height: '400px', width: '100%', marginBottom: '1rem', background: '#eee' }}>
                    {loadingRelayPoints && <div className="live-buy-relay-map-overlay">Chargement des points...</div>}
                </div>

                {mapApiError && <div className="live-buy-relay-error">{mapApiError}</div>}
                {relayError && !loadingRelayPoints && <div className="live-buy-relay-error">{relayError}</div>}
                {!customerData.shipping_address.zip && !relayError && (
                  <div className="live-buy-relay-instruction">Entrez votre code postal pour voir les points de relais.</div>
                )}
                {validationErrors.relay && <div className="live-buy-error">{validationErrors.relay}</div>}

                {selectedRelay && customerData.relay_id && (
                    <div className="live-buy-selected-relay-info">
                        <h4>Point Relais Sélectionné :</h4>
                        <p><strong>{selectedRelay.name}</strong></p>
                        <p>{selectedRelay.address1}</p>
                        {selectedRelay.address2 && <p>{selectedRelay.address2}</p>}
                        <p>{selectedRelay.postcode} {selectedRelay.city}</p>
                    </div>
                )}

              </div>
            ) : null}

            {paymentIntent && !customerData.click_and_collect && (
              <div className="live-buy-payment">
                <div id="payment-element"></div>
                {paymentStatus && (
                  <div className="live-buy-payment-overlay">
                    <div className="live-buy-payment-message">
                      {paymentStatus === 'success' ? (
                        <>
                          <div className="live-buy-payment-icon success">
                            <FaCheckCircle />
                          </div>
                          <p>{paymentMessage}</p>
                        </>
                      ) : (
                        <>
                          <div className="live-buy-payment-icon error">
                            <FaTimesCircle />
                          </div>
                          <p>{paymentMessage || paymentError}</p>
                        </>
                      )}
                    </div>
                  </div>
                )}
                <button 
                  type="button"
                  className="live-buy-button live-buy-button-primary"
                  onClick={handlePaymentConfirmation}
                  disabled={!stripe || !elements || processing || paymentStatus === 'success'}
                >
                  {processing ? 'Processing...' : 'Pay Now'}
                </button>
              </div>
            )}

            <div className="live-buy-footer">
              <div className="live-buy-total">
                <div className="live-buy-shipping">
                  <FaTruck />
                  <span>
                    {(paymentIntent || paymentStatus) && (
                      isRepeatCustomer || customerData.click_and_collect
                        ? 'Shipping: Free'
                        : `Shipping: ${formData.shipping_fee.toFixed(2)} €`
                    )}
                  </span>
                </div>
                <div className="live-buy-total-amount">
                  {customerData.click_and_collect ? (
                    "payer en magasin"
                  ) : (
                    <>
                      Total: {(
                        selectedProducts.reduce((sum, product) => {
                          const productTotal = product.configuredVariants?.reduce((variantSum, cv) => 
                            variantSum + (cv.variantData.price * cv.quantity), 0) || 0;
                          return sum + productTotal;
                        }, 0) 
                        + ((paymentIntent || paymentStatus) ? 
                            (isRepeatCustomer || selectedVariants.length === 0 ? 0 : formData.shipping_fee) 
                            : 0) // Note: for click_and_collect, shipping is effectively free, so the conditional add for shipping already handles this
                      ).toFixed(2)} €
                    </>
                  )}
                </div>
              </div>

              <div className="live-buy-actions">
                <button 
                  type="button" 
                  className="live-buy-button live-buy-button-secondary"
                  onClick={() => setStep(1)}
                >
                  Retour
                </button>
                {!paymentIntent ? (
                  <button 
                    className="live-buy-button live-buy-button-primary"
                  >
                    Valider
                  </button>
                ) : null}
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default LiveBuyForm;
